'use client'

import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Building2,
  Database,
  Layers,
  MessageSquare,
  LockKeyhole,
  Cloud,
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Users,
  TrendingUp,
  Globe,
  Zap,
  Star,
  Package,
  ShoppingCart,
  Target,
  FileText,
  Wallet,
  BarChart3,
  CheckSquare,
  Check,
  Search,
  Sparkles,
  AlertCircle,
  X,
  CreditCard,
  Receipt,
  Eye as EyeIcon,
  Wrench,
  ClipboardList,
  FolderTree,
  Calendar,
  BookUser,
  StickyNote,
} from 'lucide-react'
import { useAppStore } from '@/stores/app-store'
import { Checkbox } from '@/components/ui/checkbox'
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '@/components/ui/input-otp'

/* ═══════════════════════════════════════════════════════════════
   Shared Constants & Helpers
   ═══════════════════════════════════════════════════════════════ */

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
}

const floatAnimation = (delay: number) => ({
  y: [0, -8, 0],
  transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' as const, delay },
})

/* ── Logo Mark ──────────────────────────────────────────────── */
function LogoMark({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const [branding, setBranding] = useState<{ app_name: string; logo_url: string }>({ app_name: 'ORYXOS', logo_url: '' })

  useEffect(() => {
    fetch('/api/branding')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.settings) {
          setBranding({
            app_name: data.settings.app_name || 'ORYXOS',
            logo_url: data.settings.logo_url || ''
          })
        }
      })
      .catch(() => { })
  }, [])

  const s = size === 'sm' ? 'w-7 h-7 text-xs font-bold' : 'w-9 h-9 text-sm font-bold'

  // Custom letter badge fallback
  const firstLetter = (branding.app_name || 'ORYXOS').charAt(0).toUpperCase()

  // Image URL validation helper
  const isValidImageUrl = (url?: string) => {
    if (!url) return false
    return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/') || url.startsWith('blob:')
  }

  return (
    <div className="flex items-center gap-2.5">
      {isValidImageUrl(branding.logo_url) ? (
        <img
          src={branding.logo_url}
          alt={branding.app_name}
          className={`${size === 'sm' ? 'h-7 max-w-[120px]' : 'h-9 max-w-[150px]'} object-contain`}
          onError={(e) => {
            // Replace with avatar fallback on loading errors
            e.currentTarget.style.display = 'none'
            const sibling = e.currentTarget.nextElementSibling as HTMLElement
            if (sibling) sibling.style.display = 'flex'
          }}
        />
      ) : null}

      {/* Fallback Letter Badge */}
      {(!isValidImageUrl(branding.logo_url)) && (
        <div className={`${s} rounded-[4px] bg-[#0d75e3] flex items-center justify-center text-white font-bold shadow-sm shadow-[#0d75e3]/20 shrink-0`}>
          {firstLetter}
        </div>
      )}

      <span className={`${size === 'sm' ? 'text-base' : 'text-xl'} font-extrabold tracking-tight text-black dark:text-white`}>
        {(branding.app_name || 'ORYXOS').slice(0, -2)}
        <span className="text-[#0d75e3]">{(branding.app_name || 'ORYXOS').slice(-2)}</span>
      </span>
    </div>
  )
}

/* ── Social Login Grid ─────────────────────────────────────── */
function SocialLoginGrid() {
  const providers = [
    { name: 'Google', icon: 'G', color: 'hover:bg-red-50 dark:hover:bg-red-950/30' },
    { name: 'Microsoft', icon: 'M', color: 'hover:bg-blue-50 dark:hover:bg-blue-950/30' },
    { name: 'Apple', icon: 'A', color: 'hover:bg-gray-50 dark:hover:bg-gray-800/30' },
  ]
  return (
    <div className="grid grid-cols-3 gap-3">
      {providers.map((p) => (
        <button key={p.name} type="button" className={`flex items-center justify-center gap-2 h-11 border border-border rounded-[4px] text-sm font-medium transition-colors ${p.color}`}>
          <span className="w-5 h-5 flex items-center justify-center text-xs font-bold rounded-[2px] bg-muted">{p.icon}</span>
          <span className="hidden sm:inline">{p.name}</span>
        </button>
      ))}
    </div>
  )
}

/* ── Campaign Side (Right Panel) ───────────────────────────── */
interface CampaignItem {
  id: string
  imageUrl?: string | null
  videoUrl?: string | null
  tagline?: string | null
  companyName?: string | null
  link?: string | null
  campaignType: string
  mediaUrls: string[]
}

function CampaignSide({ variant = 'login' }: { variant?: 'login' | 'register' }) {
  const defaultSlides = [
    { title: 'Unified Business Control', desc: 'Manage your entire lifecycle from one secure platform.', icon: ShieldCheck, color: 'text-emerald-400' },
    { title: 'Next-Gen Performance', desc: 'Experience lightning-fast response times and real-time syncing.', icon: Zap, color: 'text-amber-400' },
    { title: 'Global Reach', desc: 'Built for teams working across borders and timezones.', icon: Globe, color: 'text-cyan-400' },
  ]

  const [current, setCurrent] = useState(0)
  const [selectedMods, setSelectedMods] = useState<string[]>([])
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([])
  const [activeCampaignIdx, setActiveCampaignIdx] = useState(0)
  const [showDefault, setShowDefault] = useState(false)
  const [videoEnded, setVideoEnded] = useState(false)

  // Fetch all campaigns for this page location
  useEffect(() => {
    const location = variant === 'login' ? 'signin_page' : 'signup_page'
    fetch(`/api/cpanel/campaigns?public=true&location=${location}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && Array.isArray(data.data) && data.data.length > 0) {
          const items: CampaignItem[] = data.data.map((c: any) => {
            const uiConfig = c.uiConfig || {}
            const mediaUrls = c.mediaUrls || []
            return {
              id: c.id,
              imageUrl: uiConfig.imageUrl || mediaUrls[0] || null,
              videoUrl: uiConfig.videoUrl || null,
              tagline: c.tagline || null,
              companyName: c.companyName || null,
              link: c.link || null,
              campaignType: c.campaignType || 'image',
              mediaUrls,
            }
          })
          setCampaigns(items)
        }
      })
      .catch(() => {})
  }, [variant])

  // Multi-campaign rotation timer
  useEffect(() => {
    if (campaigns.length <= 1) return
    // For multiple campaigns: rotate every 10 seconds
    const timer = setInterval(() => {
      setActiveCampaignIdx(prev => {
        const next = (prev + 1) % campaigns.length
        setVideoEnded(false)
        return next
      })
    }, 10000)
    return () => clearInterval(timer)
  }, [campaigns.length])

  // For single video campaign: when video ends, show default for 5s then loop
  useEffect(() => {
    if (!videoEnded || campaigns.length > 1) return
    // Single video ended — show default blue screen briefly
    setShowDefault(true)
    const timer = setTimeout(() => {
      setShowDefault(false)
      setVideoEnded(false)
    }, 5000)
    return () => clearTimeout(timer)
  }, [videoEnded, campaigns.length])

  // Default slides rotation (when no campaign or during default screen)
  useEffect(() => {
    if (campaigns.length > 0 && !showDefault) return
    const timer = setInterval(() => setCurrent(p => (p + 1) % defaultSlides.length), 5000)
    return () => clearInterval(timer)
  }, [defaultSlides.length, campaigns.length, showDefault])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('oryxos_selected_modules')
      if (saved) {
        try {
          const parsed = JSON.parse(saved)
          if (Array.isArray(parsed) && parsed.length > 0) setSelectedMods(parsed)
          else setSelectedMods(['Messaging'])
        } catch { setSelectedMods(['Messaging']) }
      } else { setSelectedMods(['Messaging']) }
    }
  }, [])

  // Active campaign to display
  const activeCampaign = campaigns[activeCampaignIdx] || null
  const hasCampaigns = campaigns.length > 0 && !showDefault

  // Render campaign media
  if (hasCampaigns && activeCampaign && (activeCampaign.imageUrl || activeCampaign.videoUrl)) {
    const isYouTube = activeCampaign.videoUrl && /youtube\.com|youtu\.be|vimeo\.com/.test(activeCampaign.videoUrl)
    const isDirectVideo = activeCampaign.videoUrl && !isYouTube

    return (
      <div className="hidden lg:flex relative overflow-hidden bg-black">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCampaign.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0"
          >
            {/* Media */}
            {isYouTube ? (
              <iframe
                src={activeCampaign.videoUrl!.replace('https://youtube.com/', 'https://www.youtube.com/').replace('youtube.com/shorts/', 'www.youtube.com/embed/').replace('www.youtube.com/shorts/', 'www.youtube.com/embed/').replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/').split('?si=')[0].split('?')[0] + '?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1'}
                className="absolute inset-0 w-full h-full border-0"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            ) : isDirectVideo ? (
              <video
                src={activeCampaign.videoUrl!}
                autoPlay
                muted
                playsInline
                loop={campaigns.length > 1}
                className="absolute inset-0 w-full h-full object-cover"
                onEnded={() => {
                  if (campaigns.length === 1) setVideoEnded(true)
                  else setActiveCampaignIdx(prev => (prev + 1) % campaigns.length)
                }}
              />
            ) : activeCampaign.imageUrl ? (
              <img src={activeCampaign.imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : null}
          </motion.div>
        </AnimatePresence>

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/20 z-10" />

        {/* Content overlay */}
        <div className="relative z-20 flex flex-col items-start justify-end p-12 w-full">
          {activeCampaign.companyName && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/60 mb-2">
              {activeCampaign.companyName}
            </span>
          )}
          {activeCampaign.tagline && (
            <h2 className="text-2xl font-bold text-white leading-tight max-w-md">{activeCampaign.tagline}</h2>
          )}
          {activeCampaign.link && (
            <a href={activeCampaign.link} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-[4px] bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-semibold transition-colors backdrop-blur-sm">
              Learn More <ArrowRight className="size-3" />
            </a>
          )}
          {/* Campaign indicator dots (multiple campaigns) */}
          {campaigns.length > 1 && (
            <div className="flex items-center gap-1.5 mt-4">
              {campaigns.map((_, i) => (
                <button key={i} onClick={() => { setActiveCampaignIdx(i); setVideoEnded(false) }} className={`transition-all rounded-full ${i === activeCampaignIdx ? 'w-5 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/60'}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  const selectedConfigs = selectedMods.map(name => MODULE_CATALOG.find(m => m.name === name)).filter(Boolean) as ModuleConfig[]

  if (variant === 'register') {
    return (
      <div className="hidden lg:flex relative overflow-hidden flex-col items-center justify-center p-12 text-center text-white" style={{ background: 'linear-gradient(180deg, #0d75e3 0%, #00c3f7 100%)' }}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-indigo-400/20 blur-3xl" />
        </div>

        <div className="relative z-10 w-full max-w-md flex flex-col items-center">
          <div className="space-y-6">
            <h2 className="text-2xl font-bold tracking-tight leading-[1.3]">
              Everything you need<br />to run your business
            </h2>

            <div className="space-y-4 pt-6 text-left max-w-xs mx-auto font-semibold text-white/95 text-sm">
              <div className="flex items-center gap-3">
                <span className="text-white text-lg select-none">✓</span>
                <span>All modules included in trial</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-white text-lg select-none">✓</span>
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-white text-lg select-none">✓</span>
                <span>14-day free trial</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-white text-lg select-none">✓</span>
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>

          {/* Selected Modules Frosted Card */}
          <div className="mt-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-[8px] p-5 w-full max-w-sm text-left shadow-lg">
            <h3 className="text-[10px] font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center justify-between">
              <span>Selected Modules</span>
              <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] text-white">
                {selectedConfigs.length} {selectedConfigs.length === 1 ? 'Module' : 'Modules'}
              </span>
            </h3>

            <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1 custom-scrollbar">
              {selectedConfigs.map((mod) => {
                const Icon = mod.icon
                const isFree = mod.price === 0
                return (
                  <div key={mod.name} className="flex items-center justify-between p-2 rounded-[4px] bg-white/5 hover:bg-white/10 transition-colors border border-white/5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-[4px] bg-white/15 flex items-center justify-center text-white shrink-0">
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs font-semibold text-white leading-none truncate">{mod.name}</h4>
                      </div>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${isFree ? 'text-emerald-200 bg-emerald-500/20' : 'text-white bg-white/15'}`}>
                      {isFree ? 'FREE' : `$${mod.price}/mo`}
                    </span>
                  </div>
                )
              })}
            </div>

            {/* Totals */}
            <div className="mt-4 pt-3 border-t border-white/10 space-y-1.5">
              {(() => {
                const paidTotal = selectedConfigs.filter(m => m.price > 0).reduce((s, m) => s + m.price, 0)
                const freeCount = selectedConfigs.filter(m => m.price === 0).length
                return (
                  <>
                    {freeCount > 0 && (
                      <div className="flex justify-between items-center text-[11px] text-white/70">
                        <span>Stack Apps ({freeCount})</span>
                        <span className="text-emerald-300 font-semibold">FREE</span>
                      </div>
                    )}
                    {paidTotal > 0 && (
                      <div className="flex justify-between items-center text-[11px] text-white/70">
                        <span>Paid Modules</span>
                        <span className="font-semibold">${paidTotal}/mo</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-xs font-bold text-white pt-1.5 border-t border-white/10">
                      <span className="uppercase tracking-wider">Total</span>
                      <span className="text-sm font-extrabold bg-[#0f79ea] px-2.5 py-1 rounded-[4px] shadow-sm">
                        {paidTotal > 0 ? `$${paidTotal}/user/mo` : 'FREE'}
                      </span>
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Fallback: default animated slides
  return (
    <div className="hidden lg:flex relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0d75e3 0%, #00c3f7 100%)' }}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-white/20 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-indigo-400/20 blur-3xl" />
      </div>
      <div className="relative z-10 flex flex-col items-center justify-center p-12 w-full">
        <AnimatePresence mode="wait">
          <motion.div key={current} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.5 }} className="text-center space-y-6 max-w-md mb-12">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-white/10 flex items-center justify-center border border-white/20 shadow-inner backdrop-blur-sm">
              {(() => { const Icon = defaultSlides[current].icon; return <Icon className={`w-10 h-10 ${defaultSlides[current].color}`} /> })()}
            </div>
            <h2 className="text-3xl font-bold text-white tracking-tight">{defaultSlides[current].title}</h2>
            <p className="text-white/70 text-sm leading-relaxed">{defaultSlides[current].desc}</p>
          </motion.div>
        </AnimatePresence>
        <div className="flex justify-center gap-2 mt-8">
          {defaultSlides.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)} className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-white w-6' : 'bg-white/30 hover:bg-white/50'}`} />
          ))}
        </div>
        <div className="mt-12 space-y-4 w-full max-w-sm">
          {[
            { icon: Users, label: 'Active Users', value: '2,847', color: 'from-cyan-400/20 to-blue-500/20' },
            { icon: TrendingUp, label: 'Revenue Growth', value: '+23.5%', color: 'from-emerald-400/20 to-green-500/20' },
            { icon: Globe, label: 'Global Reach', value: '42 Countries', color: 'from-violet-400/20 to-purple-500/20' },
          ].map((card, i) => (
            <motion.div key={card.label} animate={floatAnimation(i * 0.6)} className={`flex items-center gap-4 p-4 rounded-[4px] bg-gradient-to-r ${card.color} backdrop-blur-xl border border-white/15`}>
              <div className="w-10 h-10 rounded-[4px] bg-white/15 flex items-center justify-center">
                <card.icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-white/60 font-medium">{card.label}</p>
                <p className="text-lg font-bold text-white">{card.value}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════
   Module & Pricing Configuration
   ═══════════════════════════════════════════════════════════════ */

interface ModuleConfig {
  name: string
  description: string
  icon: React.ElementType
  color: string
  text: string
  price: number
  priceLabel: string
  features: string[]
  category: string
  popular?: boolean
}

const MODULE_CATALOG: ModuleConfig[] = [
  { name: 'CRM', description: 'Customer Relationship Management', icon: Users, color: 'from-blue-500/20 to-cyan-500/20', text: 'text-blue-500', price: 12, priceLabel: '/user/mo', features: ['Contact Management', 'Lead Tracking', 'Sales Pipeline'], category: 'Sales' },
  { name: 'Sales', description: 'Sales Order Management', icon: BarChart3, color: 'from-sky-500/20 to-blue-500/20', text: 'text-sky-500', price: 10, priceLabel: '/user/mo', features: ['Quotations', 'Invoicing', 'Order Processing'], category: 'Sales' },
  { name: 'HRM', description: 'Human Resource Management', icon: Star, color: 'from-pink-500/20 to-rose-500/20', text: 'text-pink-500', price: 15, priceLabel: '/user/mo', features: ['Employee Records', 'Attendance', 'Leave Management'], category: 'People', popular: true },
  { name: 'Inventory', description: 'Stock & Warehouse Management', icon: Package, color: 'from-emerald-500/20 to-green-500/20', text: 'text-emerald-500', price: 12, priceLabel: '/user/mo', features: ['Stock Tracking', 'Warehouses', 'Reorder Alerts'], category: 'Operations' },
  { name: 'Procurement', description: 'Purchase & Supplier Management', icon: ShoppingCart, color: 'from-teal-500/20 to-emerald-500/20', text: 'text-teal-500', price: 10, priceLabel: '/user/mo', features: ['Purchase Orders', 'Supplier Mgmt', 'RFQ Processing'], category: 'Operations' },
  { name: 'Project Management', description: 'Project Planning & Execution', icon: Target, color: 'from-violet-500/20 to-purple-500/20', text: 'text-violet-500', price: 12, priceLabel: '/user/mo', features: ['Project Tracking', 'Task Management', 'Milestones'], category: 'Operations', popular: true },
  { name: 'Billing', description: 'Invoicing & Financial Tracking', icon: CreditCard, color: 'from-emerald-500/20 to-teal-500/20', text: 'text-emerald-600', price: 10, priceLabel: '/user/mo', features: ['Invoicing', 'Recurring Billing', 'Credit Notes'], category: 'Finance' },
  { name: 'Expenses', description: 'Expense Tracking & Approvals', icon: Receipt, color: 'from-red-500/20 to-orange-500/20', text: 'text-red-500', price: 8, priceLabel: '/user/mo', features: ['Expense Claims', 'Approvals', 'Budget Tracking'], category: 'Finance' },
  { name: 'Finance', description: 'Financial Management & Reporting', icon: Wallet, color: 'from-amber-500/20 to-orange-500/20', text: 'text-amber-500', price: 15, priceLabel: '/user/mo', features: ['Accounts Receivable', 'Payable', 'Financial Reports'], category: 'Finance' },
  { name: 'Documents', description: 'Document Storage & Management', icon: FileText, color: 'from-slate-500/20 to-gray-500/20', text: 'text-slate-500', price: 8, priceLabel: '/user/mo', features: ['File Storage', 'Version Control', 'Sharing'], category: 'Productivity' },
  { name: 'Forms', description: 'Form Builder & Submissions', icon: ClipboardList, color: 'from-sky-500/20 to-cyan-500/20', text: 'text-sky-500', price: 8, priceLabel: '/user/mo', features: ['Drag & Drop Builder', 'Submissions', 'Analytics'], category: 'Productivity' },
  { name: 'Assets', description: 'Asset Tracking & Maintenance', icon: Wrench, color: 'from-orange-500/20 to-amber-500/20', text: 'text-orange-500', price: 10, priceLabel: '/user/mo', features: ['Asset Registry', 'Maintenance', 'Depreciation'], category: 'Operations' },
  { name: 'Tasks', description: 'Task & To-Do Management', icon: CheckSquare, color: 'from-green-500/20 to-emerald-500/20', text: 'text-green-500', price: 8, priceLabel: '/user/mo', features: ['Task Boards', 'Assignments', 'Deadlines'], category: 'Productivity' },
  { name: 'Analytics', description: 'Business Intelligence & Reports', icon: TrendingUp, color: 'from-indigo-500/20 to-violet-500/20', text: 'text-indigo-500', price: 12, priceLabel: '/user/mo', features: ['Dashboards', 'Custom Reports', 'KPI Tracking'], category: 'Intelligence' },
  { name: 'Law Firm', description: 'Legal Case & Client Management', icon: Layers, color: 'from-slate-500/20 to-gray-500/20', text: 'text-slate-600', price: 15, priceLabel: '/user/mo', features: ['Case Management', 'Client Portal', 'Billing'], category: 'Operations' },
  { name: 'Vendor Management', description: 'Supplier & Vendor Tracking', icon: Package, color: 'from-cyan-500/20 to-teal-500/20', text: 'text-cyan-600', price: 10, priceLabel: '/user/mo', features: ['Vendor Registry', 'Contracts', 'Performance'], category: 'Operations' },
  // Stack Apps (Free)
  { name: 'Messaging', description: 'Internal Communication Hub', icon: MessageSquare, color: 'from-cyan-500/20 to-blue-500/20', text: 'text-cyan-500', price: 0, priceLabel: 'Free', features: ['Direct Messages', 'Group Chats', 'Notifications'], category: 'Communication' },
  { name: 'Sticky Notes', description: 'Quick Notes & Reminders', icon: StickyNote, color: 'from-yellow-500/20 to-amber-500/20', text: 'text-yellow-500', price: 0, priceLabel: 'Free', features: ['Floating Notes', 'Pin & Organize', 'Color Coded'], category: 'Productivity' },
  { name: 'Security Center', description: 'Security Monitoring & Logs', icon: ShieldCheck, color: 'from-red-500/20 to-rose-500/20', text: 'text-red-500', price: 0, priceLabel: 'Free', features: ['Audit Logs', 'Security Events', 'Alerts'], category: 'Intelligence' },
  { name: 'Calendar', description: 'Event Scheduling & Planning', icon: Calendar, color: 'from-rose-500/20 to-pink-500/20', text: 'text-rose-500', price: 0, priceLabel: 'Free', features: ['Events', 'Scheduling', 'Reminders'], category: 'Productivity' },
  { name: 'Visitors', description: 'Visitor Registration & Tracking', icon: Users, color: 'from-violet-500/20 to-purple-500/20', text: 'text-violet-500', price: 8, priceLabel: '/user/mo', features: ['Check-in/out', 'Visitor Logs', 'Notifications'], category: 'People' },
  { name: 'QuickDrop', description: 'Quick Clipboard & Snippets', icon: Zap, color: 'from-emerald-500/20 to-teal-500/20', text: 'text-emerald-500', price: 0, priceLabel: 'Free', features: ['Quick Paste', 'Snippets', 'Pin Items'], category: 'Productivity' },
  { name: 'Contacts', description: 'Personal Contact Directory', icon: BookUser, color: 'from-sky-500/20 to-blue-500/20', text: 'text-sky-500', price: 0, priceLabel: 'Free', features: ['Contact Cards', 'Search', 'Import/Export'], category: 'People' },
]

/* ═══════════════════════════════════════════════════════════════
   LoginPage — Split View
   ═══════════════════════════════════════════════════════════════ */
export function LoginPage() {
  const setCurrentView = useAppStore((s) => s.setCurrentView)
  const setIsAuthenticated = useAppStore((s) => s.setIsAuthenticated)
  const setUserId = useAppStore((s) => s.setUserId)
  const setUserName = useAppStore((s) => s.setUserName)
  const setUserEmail = useAppStore((s) => s.setUserEmail)
  const setCompanyName = useAppStore((s) => s.setCompanyName)
  const setTenantSlug = useAppStore((s) => s.setTenantSlug)
  const setUserRole = useAppStore((s) => s.setUserRole)
  const setSelectedModules = useAppStore((s) => s.setSelectedModules)
  const setUserPermissions = useAppStore((s) => s.setUserPermissions)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [blockedInfo, setBlockedInfo] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    setError('')
    const form = new FormData(e.currentTarget)
    const username = form.get('username') as string
    const password = form.get('password') as string
    try {
      const res = await fetch('/api/cpanel/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      })
      const data = await res.json()
      if (!res.ok) {
        if (data.code === 'TENANT_BLOCKED') {
          setBlockedInfo(data.error || 'Your workspace has been temporarily suspended. Please contact our support team.')
          return
        }
        setError(data.error || 'Login failed'); return
      }
      setUserId(data.user?.id || '')
      setUserName(data.user?.fullName || data.user?.username || 'User')
      setUserEmail(data.user?.email || '')
      setCompanyName(data.tenant?.companyName || 'ORYXOS Inc.')
      setTenantSlug(data.tenant?.slug || '')
      setUserRole(data.user?.role || 'User')
      // Store selected modules from tenant
      if (data.tenant?.selectedModules) {
        setSelectedModules(data.tenant.selectedModules)
      }
      if (data.user?.permissions) {
        setUserPermissions(data.user.permissions)
      }
      setIsAuthenticated(true)
      setCurrentView('dashboard')
    } catch { setError('Network error. Please try again.') }
    finally { setLoading(false) }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: Form */}
      <div className="flex items-center justify-center p-6 sm:p-8 lg:p-12 relative bg-[#F9FAFB] dark:bg-background">
        <button onClick={() => setCurrentView('landing')} className="absolute top-6 left-6 flex items-center gap-2 p-2 hover:bg-gray-200 dark:hover:bg-accent rounded-[4px] transition-colors group z-10">
          <ArrowLeft className="w-4 h-4 text-gray-500 group-hover:text-gray-900 dark:group-hover:text-foreground" />
          <span className="text-xs font-bold text-gray-500 group-hover:text-gray-900 dark:group-hover:text-foreground uppercase tracking-widest">Home</span>
        </button>
        <motion.div variants={fadeUp} initial="initial" animate="animate" className="w-full max-w-[440px] space-y-8">
          <div className="space-y-2">
            <LogoMark />
            <h1 className="text-2xl font-bold tracking-tight pt-2">Welcome back</h1>
            <p className="text-sm text-muted-foreground">Sign in to access your workspace</p>
          </div>
          {error && <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[4px] flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

          {/* Workspace suspended / blocked modal */}
          <AnimatePresence>
            {blockedInfo && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                onClick={() => setBlockedInfo(null)}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  className="w-full max-w-md bg-white dark:bg-card border border-border rounded-[4px] shadow-2xl overflow-hidden text-left"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-7 flex flex-col items-center text-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center">
                      <ShieldAlert className="w-7 h-7 text-red-600 dark:text-red-400" />
                    </div>
                    <h2 className="text-lg font-bold text-foreground">Workspace Suspended</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">{blockedInfo}</p>
                  </div>
                  <div className="flex gap-2 p-4 border-t border-border bg-secondary/30">
                    <button
                      type="button"
                      onClick={() => setBlockedInfo(null)}
                      className="flex-1 h-10 rounded-[4px] border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => { setBlockedInfo(null); setCurrentView('contact') }}
                      className="flex-1 h-10 rounded-[4px] bg-[#0078D4] text-white text-sm font-semibold hover:bg-[#005A9E] transition-colors"
                    >
                      Contact Us
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          <form onSubmit={handleSubmit} className="space-y-4 bg-white dark:bg-card p-8 rounded-[4px] shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] border border-gray-100 dark:border-border">
            <div className="space-y-1.5">
              <label htmlFor="login-username" className="text-[14px] font-semibold text-[#111827] dark:text-foreground">Email or Username</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input id="login-username" name="username" type="text" required autoComplete="username" placeholder="admin" className="w-full h-11 bg-secondary/50 border border-border rounded-[4px] pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/20 focus:border-[#0078D4] transition-colors placeholder:text-muted-foreground" />
              </div>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="login-password" className="text-[14px] font-semibold text-[#111827] dark:text-foreground">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input id="login-password" name="password" type={showPassword ? 'text' : 'password'} required autoComplete="current-password" placeholder="••••••••" className="w-full h-11 bg-secondary/50 border border-border rounded-[4px] pl-10 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/20 focus:border-[#0078D4] transition-colors placeholder:text-muted-foreground" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox checked={rememberMe} onCheckedChange={(v) => setRememberMe(v === true)} id="remember" />
                <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer select-none">Remember me</label>
              </div>
              <button type="button" onClick={() => setCurrentView('forgot-password')} className="text-sm font-medium text-[#0078D4] hover:text-[#005A9E] transition-colors">Forgot password?</button>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-[#0078D4] text-white hover:bg-[#005A9E] rounded-[4px] h-11 font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div className="relative"><div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div><div className="relative flex justify-center text-xs"><span className="bg-white dark:bg-card px-2 text-muted-foreground">or continue with</span></div></div>
            <SocialLoginGrid />
            <div className="text-center pt-2">
              <p className="text-sm text-muted-foreground">Don&apos;t have an account?{' '}<button type="button" onClick={() => setCurrentView('module-selection')} className="font-semibold text-[#0078D4] hover:text-[#005A9E] transition-colors">Sign up</button></p>
            </div>
          </form>
        </motion.div>
      </div>
      {/* Right: Campaign */}
      <CampaignSide variant="login" />
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════
   RegisterPage — Split View with Real Multi-Tenant Provisioning
   
   Implements the 10-step Enterprise SaaS Provisioning flow:
   1. Validation → 2. Workspace → 3. Tenant Record → 4. Database
   5. User Created → 6. Identity Sync → 7. Profile File → 8. Session
   9. Complete → 10. Auto-login redirect
   
   The registration API handles all provisioning server-side and
   returns a session cookie automatically (no separate login needed).
   ═══════════════════════════════════════════════════════════════ */
export function RegisterPage() {
  const setCurrentView = useAppStore((s) => s.setCurrentView)
  const setIsAuthenticated = useAppStore((s) => s.setIsAuthenticated)
  const setUserName = useAppStore((s) => s.setUserName)
  const setUserEmail = useAppStore((s) => s.setUserEmail)
  const setCompanyName = useAppStore((s) => s.setCompanyName)
  const setTenantSlug = useAppStore((s) => s.setTenantSlug)
  const setSelectedModules = useAppStore((s) => s.setSelectedModules)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [agreedTerms, setAgreedTerms] = useState(false)
  const [provisioningStep, setProvisioningStep] = useState(-1)
  const [success, setSuccess] = useState(false)

  // Real-time Workspace Name Check
  const [workspaceName, setWorkspaceName] = useState('')
  const [workspaceAvailable, setWorkspaceAvailable] = useState<boolean | null>(null)
  const [workspaceChecking, setWorkspaceChecking] = useState(false)
  const [workspaceSuggestion, setWorkspaceSuggestion] = useState('')

  // Real-time Email Verification States
  const [email, setEmail] = useState('')
  const [checkingEmail, setCheckingEmail] = useState(false)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [maskedEmail, setMaskedEmail] = useState('')

  // Real provisioning steps matching the backend — shown as centered modal
  const provisioningSteps = [
    { icon: ShieldCheck, label: 'Validation', desc: 'Verifying registration inputs' },
    { icon: Building2, label: 'Workspace', desc: 'Creating isolated directory structure' },
    { icon: Database, label: 'Database', desc: 'Provisioning tenant database' },
    { icon: Layers, label: 'Modules', desc: 'Configuring selected modules' },
    { icon: Users, label: 'Admin User', desc: 'Creating super admin account' },
    { icon: LockKeyhole, label: 'Security', desc: 'Setting up session & encryption' },
    { icon: Cloud, label: 'Finalizing', desc: 'Syncing identity & profile' },
  ]

  const progressPercent = Math.round(((provisioningStep + 1) / provisioningSteps.length) * 100)

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    if (loading && !success && provisioningStep >= 0) {
      interval = setInterval(() => setProvisioningStep((prev) => (prev < provisioningSteps.length - 1 ? prev + 1 : prev)), 800)
    }
    return () => clearInterval(interval)
  }, [loading, success, provisioningStep, provisioningSteps.length])

  async function handleWorkspaceBlur() {
    if (!workspaceName || workspaceName.trim().length < 2) return
    setWorkspaceChecking(true)
    setWorkspaceAvailable(null)
    setWorkspaceSuggestion('')
    try {
      const res = await fetch('/api/cpanel/auth/check-workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyName: workspaceName.trim() })
      })
      const data = await res.json()
      setWorkspaceAvailable(data.available)
      if (!data.available && data.suggestion) setWorkspaceSuggestion(data.suggestion)
    } catch { setWorkspaceAvailable(null) }
    setWorkspaceChecking(false)
  }

  async function handleEmailBlur() {
    if (!email || !email.includes('@')) return
    setCheckingEmail(true)
    try {
      const res = await fetch('/api/cpanel/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })
      const data = await res.json()
      if (data.exists) {
        setMaskedEmail(data.maskedEmail)
        setShowDuplicateModal(true)
        setEmail('')
        setAgreedTerms(false)
      }
    } catch { }
    setCheckingEmail(false)
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!agreedTerms) { setError('You must agree to the terms and conditions.'); return }
    setLoading(true); setError(''); setProvisioningStep(0)

    const form = new FormData(e.currentTarget)
    const body = {
      companyName: form.get('companyName') as string,
      fullName: form.get('fullName') as string,
      email: form.get('email') as string,
      password: form.get('password') as string,
    }
    const selectedMods = localStorage.getItem('oryxos_selected_modules') || '["Messaging"]'

    try {
      const res = await fetch('/api/cpanel/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, selectedModules: selectedMods }),
      })
      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Registration failed')
        setLoading(false)
        setProvisioningStep(-1)
        return
      }

      // Registration successful — all provisioning done server-side
      // Session cookie is already set by the registration API
      setProvisioningStep(provisioningSteps.length - 1)
      setSuccess(true)
      setSelectedModules(JSON.parse(selectedMods))

      // The registration API already created the session cookie,
      // so we just need to update the store and redirect
      setUserName(body.fullName)
      setUserEmail(body.email)
      setCompanyName(body.companyName)
      setTenantSlug(data.tenant?.slug || data.slug || '')
      setIsAuthenticated(true)

      // Mark that wizard should show on first dashboard load
      try { 
        localStorage.setItem('oryxos_wizard_pending', '1')
        localStorage.removeItem('oryxos_wizard_completed') // Clear any previous tenant's flag
      } catch {}

      // Small delay to show success state, then redirect
      setTimeout(() => {
        setCurrentView('dashboard')
      }, 1500)
    } catch {
      setError('Network error. Please try again.')
      setLoading(false)
      setProvisioningStep(-1)
    }
    finally { setLoading(false) }
  }

  // ── Provisioning Modal Overlay ──
  // Shows a centered modal card with setup animation when loading
  if (loading && provisioningStep >= 0) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="w-full max-w-md bg-white dark:bg-card rounded-[8px] shadow-2xl shadow-black/25 border border-gray-200 dark:border-border overflow-hidden"
        >
          {/* Header */}
          <div className="px-8 pt-8 pb-4 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
              className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#0078D4] to-[#005A9E] flex items-center justify-center shadow-lg shadow-[#0078D4]/25"
            >
              <Database className="w-8 h-8 text-white" />
            </motion.div>
            <h2 className="text-xl font-bold text-[#111827] dark:text-foreground tracking-tight">Setting up your workspace</h2>
            <p className="text-sm text-muted-foreground mt-1">Please wait while we configure everything</p>
          </div>

          {/* Progress Bar */}
          <div className="px-8 py-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-semibold text-[#0078D4]">Progress</span>
              <span className="text-xs font-bold text-[#111827] dark:text-foreground">{progressPercent}%</span>
            </div>
            <div className="h-2 w-full bg-gray-100 dark:bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#0078D4] to-[#00A4EF] rounded-full"
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            </div>
          </div>

          {/* Steps List */}
          <div className="px-8 py-4 space-y-1">
            {provisioningSteps.map((step, idx) => {
              const isCompleted = idx < provisioningStep
              const isActive = idx === provisioningStep
              // idx > provisioningStep = pending step
              return (
                <motion.div
                  key={step.label}
                  initial={isActive ? { x: -8, opacity: 0 } : {}}
                  animate={isActive ? { x: 0, opacity: 1 } : {}}
                  transition={{ duration: 0.3 }}
                  className={`flex items-center gap-3 py-2.5 px-3 rounded-[6px] transition-all duration-300 ${isActive ? 'bg-[#0078D4]/5 border border-[#0078D4]/15' : isCompleted ? 'opacity-70' : 'opacity-35'
                    }`}
                >
                  {/* Step Number / Status Icon */}
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold transition-all duration-300 ${isCompleted
                        ? 'bg-emerald-500 text-white'
                        : isActive
                          ? 'bg-[#0078D4] text-white shadow-md shadow-[#0078D4]/30'
                          : 'bg-gray-200 dark:bg-muted text-muted-foreground'
                      }`}
                  >
                    {isCompleted ? <Check size={14} strokeWidth={3} /> : isActive ? <Loader2 size={14} className="animate-spin" /> : idx + 1}
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold leading-tight ${isActive ? 'text-[#0078D4]' : isCompleted ? 'text-[#111827] dark:text-foreground' : 'text-muted-foreground'
                      }`}>
                      {step.label}
                    </p>
                    {isActive && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="text-[11px] text-muted-foreground mt-0.5 leading-snug"
                      >
                        {step.desc}
                      </motion.p>
                    )}
                  </div>

                  {/* Status indicator */}
                  {isCompleted && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                  {isActive && <div className="w-2 h-2 rounded-full bg-[#0078D4] animate-pulse shrink-0" />}
                </motion.div>
              )
            })}
          </div>

          {/* Footer */}
          <div className="px-8 py-4 bg-gray-50 dark:bg-muted/30 border-t border-gray-100 dark:border-border text-center">
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Your isolated workspace with a dedicated database is being provisioned. This usually takes a few seconds.
            </p>
          </div>
        </motion.div>
      </div>
    )
  }

  // ── Success State — also as centered modal ──
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-lg w-full text-center space-y-8">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}>
            <div className="w-24 h-24 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto shadow-xl shadow-emerald-500/20"><CheckCircle2 size={48} /></div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <h2 className="text-3xl font-bold text-foreground">Workspace Created!</h2>
            <p className="text-sm text-muted-foreground mt-2">Your isolated workspace is ready. Welcome to ORYXOS.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="space-y-3">
            <div className="flex items-center justify-center gap-3 py-4 px-6 bg-[#0078D4]/5 rounded-[4px] text-[#0078D4] font-medium text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to dashboard...</div>
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Workspace provisioned</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Database isolated</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Session active</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: Form */}
      <div className="flex items-center justify-center p-6 sm:p-8 lg:p-12 relative bg-[#F9FAFB] dark:bg-background overflow-y-auto">
        <button onClick={() => setCurrentView('module-selection')} className="absolute top-6 left-6 flex items-center gap-2 p-2 hover:bg-gray-200 dark:hover:bg-accent rounded-[4px] transition-colors group z-10">
          <ArrowLeft className="w-4 h-4 text-gray-500 group-hover:text-gray-900 dark:group-hover:text-foreground" />
          <span className="text-xs font-bold text-gray-500 group-hover:text-gray-900 dark:group-hover:text-foreground uppercase tracking-widest">Modules</span>
        </button>
        <motion.div variants={fadeUp} initial="initial" animate="animate" className="w-full max-w-[440px] space-y-6 py-8">
          <div className="space-y-2">
            <LogoMark />
            <h1 className="text-2xl font-bold tracking-tight pt-2">Create your workspace</h1>
            <p className="text-sm text-muted-foreground">The first user becomes the Super Admin</p>
          </div>
          {error && <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[4px] flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5 bg-white dark:bg-card p-8 rounded-[8px] shadow-[0_4px_25px_-5px_rgba(13,117,227,0.06)] border border-gray-100 dark:border-border/60">
            <div className="space-y-1.5">
              <label htmlFor="reg-companyName" className="text-[14px] font-semibold text-[#111827] dark:text-foreground">Workspace Name</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input id="reg-companyName" name="companyName" type="text" required placeholder="e.g. Acme Corp" value={workspaceName} onChange={e => setWorkspaceName(e.target.value)} onBlur={handleWorkspaceBlur} className={`w-full h-[34px] bg-secondary/30 border rounded-[8px] pl-10 pr-10 text-sm transition-all placeholder:text-muted-foreground register-input ${workspaceAvailable === false ? 'border-red-500' : workspaceAvailable === true ? 'border-emerald-500' : 'border-border/80'}`} />
                {workspaceChecking && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />}
                {!workspaceChecking && workspaceAvailable === true && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />}
                {!workspaceChecking && workspaceAvailable === false && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />}
              </div>
              {workspaceAvailable === false && (
                <p className="text-[11px] text-red-500 font-medium">This workspace name is already taken.{workspaceSuggestion && <> Try: <button type="button" onClick={() => { setWorkspaceName(workspaceSuggestion); setWorkspaceAvailable(null) }} className="text-[#0078D4] underline">{workspaceSuggestion}</button></>}</p>
              )}
              {workspaceAvailable === true && <p className="text-[11px] text-emerald-600 font-medium">✓ Workspace name is available</p>}
              {workspaceAvailable === null && <p className="text-[12px] text-muted-foreground pt-0.5">A unique workspace with its own isolated database will be provisioned. A unique domain will be auto-generated.</p>}
            </div>
            <div className="relative py-2"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div><div className="relative flex justify-center"><span className="bg-white dark:bg-card px-3 text-[12px] font-semibold text-muted-foreground uppercase tracking-widest">Admin Account</span></div></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label htmlFor="reg-fullName" className="text-[14px] font-semibold text-[#111827] dark:text-foreground">Full Name</label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input id="reg-fullName" name="fullName" type="text" required placeholder="Jane Smith" className="w-full h-[34px] bg-secondary/30 border border-border/80 rounded-[4px] pl-10 pr-4 text-sm transition-all placeholder:text-muted-foreground register-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="reg-email" className="text-[14px] font-semibold text-[#111827] dark:text-foreground">Work Email</label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${checkingEmail ? 'text-[#0078D4] animate-pulse' : 'text-muted-foreground'}`} />
                  <input id="reg-email" name="email" type="email" required placeholder="jane@acme.com" value={email} onChange={e => setEmail(e.target.value)} onBlur={handleEmailBlur} className="w-full h-[34px] bg-secondary/30 border border-border/80 rounded-[4px] pl-10 pr-10 text-sm transition-all placeholder:text-muted-foreground register-input" />
                  {checkingEmail && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                      <Loader2 className="w-4 h-4 text-[#0078D4] animate-spin" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="reg-password" className="text-[14px] font-semibold text-[#111827] dark:text-foreground">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input id="reg-password" name="password" type={showPassword ? 'text' : 'password'} required minLength={6} placeholder="Min 6 characters" className="w-full h-[34px] bg-secondary/30 border border-border/80 rounded-[4px] pl-10 pr-10 text-sm transition-all placeholder:text-muted-foreground register-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-start gap-2 pt-1">
              <Checkbox checked={agreedTerms} onCheckedChange={(v) => setAgreedTerms(v === true)} id="terms" className="mt-0.5 rounded-[4px]" />
              <label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer select-none leading-snug">
                I agree to the <button type="button" className="text-[#0d75e3] hover:text-[#0b63c5] underline transition-colors">Terms of Service</button> and <button type="button" className="text-[#0d75e3] hover:text-[#0b63c5] underline transition-colors">Privacy Policy</button>
              </label>
            </div>
            <button type="submit" disabled={loading || !agreedTerms} className="w-full bg-[#0d75e3] text-white hover:bg-[#0b63c5] rounded-[4px] h-11 font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm shadow-[#0d75e3]/10 mt-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? 'Provisioning workspace...' : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground">Already have an account?{' '}<button type="button" onClick={() => setCurrentView('login')} className="font-semibold text-[#0078D4] hover:text-[#005A9E] transition-colors">Sign in</button></p>
        </motion.div>
      </div>

      {/* Duplicate Email Modal */}
      <AnimatePresence>
        {showDuplicateModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white dark:bg-card rounded-[4px] p-8 shadow-2xl relative z-10 max-w-[480px] w-full text-center border border-gray-200 dark:border-border"
            >
              <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-6 text-amber-500 border border-amber-500/20">
                <AlertCircle size={32} />
              </div>

              <h3 className="text-[20px] font-bold text-[#111827] dark:text-foreground mb-3 tracking-tight uppercase">Email Already Registered</h3>

              <p className="text-muted-foreground text-[15px] leading-relaxed mb-8 max-w-[340px] mx-auto font-medium">
                <span className="text-[#0078D4] font-bold">{maskedEmail}</span> is already associated with an account. Would you like to sign in instead?
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <button
                  onClick={() => {
                    setShowDuplicateModal(false)
                    setCurrentView('login')
                  }}
                  className="w-full sm:w-[180px] h-[48px] bg-[#0078D4] text-white rounded-[4px] font-bold uppercase tracking-widest text-[12px] hover:bg-[#005A9E] transition-all flex items-center justify-center gap-2 shadow-sm"
                >
                  Sign In Now
                </button>

                <button
                  onClick={() => {
                    setShowDuplicateModal(false)
                    setCurrentView('forgot-password')
                  }}
                  className="w-full sm:w-[180px] h-[48px] bg-secondary/50 text-muted-foreground rounded-[4px] font-bold uppercase tracking-widest text-[12px] border border-border hover:bg-secondary transition-all flex items-center justify-center"
                >
                  Forgot Password?
                </button>
              </div>

              <button
                onClick={() => setShowDuplicateModal(false)}
                className="text-[11px] font-bold text-muted-foreground/80 uppercase tracking-[0.15em] hover:text-[#0078D4] hover:tracking-[0.2em] transition-all"
              >
                Use a different email address
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Right: Campaign */}
      <CampaignSide variant="register" />
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════
   ModuleSelectionPage — Full Page with Pricing
   ═══════════════════════════════════════════════════════════════ */
export function ModuleSelectionPage() {
  const setCurrentView = useAppStore((s) => s.setCurrentView)
  const storeSetModules = useAppStore((s) => s.setSelectedModules)

  const [searchTerm, setSearchTerm] = useState('')
  const [modules, setModules] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedModules, setSelectedModules] = useState<string[]>([])
  const [activeFilter, setActiveFilter] = useState<'all' | 'selected'>('all')

  // Icon map for dynamic modules
  const iconMap: Record<string, React.ElementType> = { Users, ShoppingCart, Package, Target, FileText, MessageSquare, Wallet, BarChart3, CheckSquare, Star, Wrench, CreditCard, Receipt, TrendingUp, ClipboardList, Layers, FolderTree }

  // Fetch modules from API
  useEffect(() => {
    fetch('/api/modules')
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data) {
          setModules(data.data)
          // Default select all stack apps
          const stackNames = data.data.filter((m: any) => m.isStackApp).map((m: any) => m.name)
          setSelectedModules(stackNames)
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const toggleModule = (name: string, isStack: boolean) => {
    // Stack apps can be unchecked but will re-select on refresh
    setSelectedModules(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name])
  }

  // Split into 3 sections sorted A-Z
  const popularModules = modules.filter(m => m.isPopular && !m.isStackApp).sort((a, b) => a.name.localeCompare(b.name))
  const stackModules = modules.filter(m => m.isStackApp).sort((a, b) => a.name.localeCompare(b.name))
  const otherModules = modules.filter(m => !m.isPopular && !m.isStackApp).sort((a, b) => a.name.localeCompare(b.name))

  // Apply search filter
  const filterBySearch = (list: any[]) => list.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase()) || (m.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    if (activeFilter === 'selected') return matchesSearch && selectedModules.includes(m.name)
    return matchesSearch
  })

  const filteredPopular = filterBySearch(popularModules)
  const filteredOther = filterBySearch(otherModules)
  const filteredStack = filterBySearch(stackModules)

  const monthlyTotal = selectedModules.reduce((sum, name) => {
    const mod = modules.find(m => m.name === name)
    return sum + (mod?.price || 0)
  }, 0)

  const renderModuleCard = (module: any) => {
    const isSelected = selectedModules.includes(module.name)
    const IconComp = iconMap[module.icon] || Package
    return (
      <motion.div key={module.id || module.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} onClick={() => toggleModule(module.name, module.isStackApp)} className={`group relative p-5 rounded-[4px] border transition-all duration-300 cursor-pointer ${isSelected ? 'border-[#0078D4] bg-[#0078D4]/5 ring-1 ring-[#0078D4]/20 shadow-[0_2px_10px_-3px_rgba(0,120,212,0.15)]' : 'border-gray-200 dark:border-border bg-white dark:bg-card hover:border-gray-300 dark:hover:border-border/80 shadow-[0_1px_3px_0_rgba(0,0,0,0.05)]'}`}>
        {module.isPopular && <div className="absolute -top-2.5 left-4 px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-[2px]">Popular</div>}
        {module.isStackApp && <div className="absolute -top-2.5 right-4 px-2 py-0.5 bg-[#0078D4] text-white text-[10px] font-bold uppercase tracking-wider rounded-[2px]">Stack</div>}
        <div className="flex items-start justify-between mb-3">
          <div className="w-11 h-11 rounded-[4px] flex items-center justify-center transition-all duration-300" style={{ backgroundColor: module.color ? `${module.color}15` : '#0078D415', color: module.color || '#0078D4' }}>
            <IconComp size={22} />
          </div>
          <div className="text-right">
            {module.price === 0 || module.isStackApp ? (
              <span className="text-sm font-bold text-emerald-500">FREE</span>
            ) : (
              <>
                <span className="text-lg font-bold text-[#111827] dark:text-foreground">${module.price}</span>
                <span className="text-[10px] text-muted-foreground block -mt-0.5">/{module.pricePeriod?.replace('per ', '') || 'mo'}</span>
              </>
            )}
          </div>
        </div>
        <div className="space-y-1 mb-3">
          <div className="flex items-center gap-1.5">
            <h3 className="font-semibold text-[14px] text-[#111827] dark:text-foreground">{module.name}</h3>
            {module.isPopular && <Star className="w-3 h-3 fill-amber-500 text-amber-500" />}
            {module.isStackApp && <Layers className="w-3 h-3 text-[#0078D4]" />}
          </div>
          <p className="text-[12px] text-muted-foreground leading-relaxed">{module.description || 'No description'}</p>
        </div>
        {module.features && module.features.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {module.features.slice(0, 2).map((f: string) => (
              <span key={f} className={`text-[10px] font-medium px-2 py-0.5 rounded-[4px] border ${isSelected ? 'text-[#0078D4] bg-[#0078D4]/10 border-[#0078D4]/20' : 'text-muted-foreground bg-muted/50 border-border'}`}>{f}</span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-border/50">
          {isSelected ? (
            <span className="text-[11px] font-semibold text-[#0078D4]">Selected</span>
          ) : (
            <span className="text-[11px] text-muted-foreground">Click to add</span>
          )}
          <div>
            {isSelected ? (
              <div className="w-5 h-5 bg-[#0078D4] text-white rounded-[4px] flex items-center justify-center shadow-sm"><Check size={12} strokeWidth={3} /></div>
            ) : (
              <div className="w-5 h-5 border border-gray-300 dark:border-border bg-white dark:bg-card rounded-[4px] group-hover:border-[#0078D4] transition-colors" />
            )}
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] dark:bg-background selection:bg-[#1972F5]/20">
      {/* Top Navbar */}
      <nav className="fixed top-0 left-0 right-0 h-14 border-b border-gray-200 dark:border-border bg-white dark:bg-card z-50">
        <div className="max-w-[1400px] mx-auto h-full px-6 flex items-center justify-between">
          <LogoMark size="sm" />
          <button onClick={() => setCurrentView('landing')} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
        </div>
      </nav>

      <div className="pt-24 pb-44">
        {/* Header */}
        <div className="max-w-[1400px] mx-auto px-6 text-center mb-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-[32px] font-bold text-[#111827] dark:text-foreground mb-3">Tailor your experience</h1>
            <p className="text-muted-foreground max-w-xl mx-auto text-sm mb-8">Select the modules that fit your business needs. You can always add more later.</p>
          </motion.div>

          <div className="max-w-md mx-auto relative group mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-[#0078D4] transition-colors" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search modules..." className="w-full h-11 bg-white dark:bg-card border border-border rounded-[4px] pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/20 focus:border-[#0078D4] transition-colors placeholder:text-muted-foreground shadow-sm" />
          </div>

          <div className="flex justify-center gap-2">
            <button onClick={() => setActiveFilter('all')} className={`px-4 py-1.5 text-xs font-semibold rounded-[4px] transition-colors ${activeFilter === 'all' ? 'bg-[#0078D4] text-white' : 'bg-white dark:bg-card border border-border text-muted-foreground hover:text-foreground'}`}>All ({modules.length})</button>
            <button onClick={() => setActiveFilter('selected')} className={`px-4 py-1.5 text-xs font-semibold rounded-[4px] transition-colors ${activeFilter === 'selected' ? 'bg-[#0078D4] text-white' : 'bg-white dark:bg-card border border-border text-muted-foreground hover:text-foreground'}`}>Selected ({selectedModules.length})</button>
          </div>
        </div>

        {/* Module Sections */}
        <div className="max-w-[1400px] mx-auto px-6 space-y-10">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-48 bg-muted/30 rounded-[4px] animate-pulse" />)}
            </div>
          ) : (
            <>
              {/* Popular Modules */}
              {filteredPopular.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#111827] dark:text-foreground">Popular Modules</h2>
                    <span className="text-xs text-muted-foreground">({filteredPopular.length})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {filteredPopular.map(renderModuleCard)}
                  </div>
                </div>
              )}

              {/* Other Modules */}
              {filteredOther.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#111827] dark:text-foreground">Modules</h2>
                    <span className="text-xs text-muted-foreground">({filteredOther.length})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {filteredOther.map(renderModuleCard)}
                  </div>
                </div>
              )}

              {/* Stack Apps */}
              {filteredStack.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Layers className="w-4 h-4 text-[#0078D4]" />
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#111827] dark:text-foreground">Stack Apps</h2>
                    <span className="text-xs text-muted-foreground">(default, free — {filteredStack.length})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {filteredStack.map(renderModuleCard)}
                  </div>
                </div>
              )}

              {filteredPopular.length === 0 && filteredOther.length === 0 && filteredStack.length === 0 && (
                <div className="py-20 text-center"><p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">No modules found matching &ldquo;{searchTerm}&rdquo;</p><button onClick={() => setSearchTerm('')} className="text-[#0078D4] uppercase text-xs font-bold tracking-widest mt-2 hover:underline">Clear Search</button></div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Bottom Sticky Bar */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-card border-t border-border z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.02)]">
        <div className="max-w-[1400px] mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <div>
              <span className="text-[12px] text-muted-foreground">Selected</span>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-foreground">{selectedModules.length} Modules</span>
                {monthlyTotal > 0 && (
                  <>
                    <div className="h-4 w-px bg-border" />
                    <span className="text-sm font-semibold text-[#0078D4]">${monthlyTotal}/user/mo</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setCurrentView('login')} className="text-sm font-medium text-muted-foreground hover:text-foreground px-5 py-2.5 rounded-[4px] transition-colors">Cancel</button>
            <button onClick={() => { localStorage.setItem('oryxos_selected_modules', JSON.stringify(selectedModules)); storeSetModules(selectedModules); setCurrentView('register') }} className="flex items-center justify-center px-6 py-2.5 rounded-[4px] font-medium text-sm bg-[#0078D4] hover:bg-[#005A9E] text-white shadow-sm transition-all hover:-translate-y-[1px]">
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════
   ForgotPasswordPage — Multi-step
   ═══════════════════════════════════════════════════════════════ */
type ForgotStep = 'email' | 'otp' | 'new-password' | 'success'

export function ForgotPasswordPage() {
  const setCurrentView = useAppStore((s) => s.setCurrentView)
  const [step, setStep] = useState<ForgotStep>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [otpExpiry, setOtpExpiry] = useState(15) // minutes, loaded from system settings
  const [canResend, setCanResend] = useState(false)

  // Load OTP expiry from system settings
  useEffect(() => {
    fetch('/api/cpanel/settings')
      .then(r => r.json())
      .then(data => {
        if (data.success && data.settings?.email_otp_expiry) {
          setOtpExpiry(Number(data.settings.email_otp_expiry) || 15)
        }
      })
      .catch(() => {})
  }, [])

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) { setCanResend(true); return }
    setCanResend(false)
    const timer = setInterval(() => setCountdown(prev => prev - 1), 1000)
    return () => clearInterval(timer)
  }, [countdown])

  async function handleEmailSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault(); setError('')
    const formEmail = (e.currentTarget.email as HTMLInputElement).value
    if (!formEmail) { setError('Please enter your email.'); return }
    setEmail(formEmail); setLoading(true)
    try {
      const res = await fetch('/api/cpanel/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formEmail }),
      })
      const data = await res.json()
      if (data.success) {
        setStep('otp')
        setCountdown(60) // 60 second cooldown before resend
      } else {
        setError(data.error || 'Failed to send verification code')
      }
    } catch {
      setError('Network error. Please try again.')
    } finally { setLoading(false) }
  }

  async function handleResend() {
    if (!canResend || !email) return
    setCanResend(false); setError('')
    try {
      const res = await fetch('/api/cpanel/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json()
      if (data.success) {
        setCountdown(60)
        setOtp('')
      } else {
        setError(data.error || 'Failed to resend code')
        setCanResend(true)
      }
    } catch {
      setError('Network error')
      setCanResend(true)
    }
  }

  async function handleOtpSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault(); setError('')
    if (otp.length < 6) { setError('Please enter the full 6-digit code.'); return }
    setLoading(true)
    try {
      const res = await fetch('/api/cpanel/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      })
      const data = await res.json()
      if (data.success) {
        setStep('new-password')
      } else {
        setError(data.error || 'Invalid or expired verification code')
      }
    } catch {
      setError('Network error. Please try again.')
    } finally { setLoading(false) }
  }

  async function handlePasswordSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault(); setError('')
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return }
    setLoading(true)
    try {
      const res = await fetch('/api/cpanel/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, newPassword }),
      })
      const data = await res.json()
      if (data.success) {
        setStep('success')
      } else {
        setError(data.error || 'Failed to reset password')
      }
    } catch {
      setError('Network error. Please try again.')
    } finally { setLoading(false) }
  }

  const stepLabels: Record<ForgotStep, string> = { email: 'Reset your password', otp: 'Verify your identity', 'new-password': 'Set new password', success: 'Password updated' }
  const stepDescriptions: Record<ForgotStep, string> = { email: 'Enter your email to receive a verification code', otp: `We sent a code to ${email.replace(/(.{2})(.*)(@.*)/, '$1***$3')}`, 'new-password': 'Create a strong password for your account', success: 'Your password has been reset successfully' }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-8 lg:p-12 relative bg-[#F9FAFB] dark:bg-background">
        <button onClick={() => setCurrentView('login')} className="absolute top-6 left-6 flex items-center gap-2 p-2 hover:bg-gray-200 dark:hover:bg-accent rounded-[4px] transition-colors group z-10">
          <ArrowLeft className="w-4 h-4 text-gray-500 group-hover:text-gray-900 dark:group-hover:text-foreground" />
          <span className="text-xs font-bold text-gray-500 group-hover:text-gray-900 dark:group-hover:text-foreground uppercase tracking-widest">Login</span>
        </button>
        <motion.div variants={fadeUp} initial="initial" animate="animate" className="w-full max-w-[420px] space-y-6">
          <div className="space-y-2"><LogoMark size="sm" /><h1 className="text-2xl font-bold tracking-tight pt-1">{stepLabels[step]}</h1><p className="text-sm text-muted-foreground">{stepDescriptions[step]}</p></div>
          {error && <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[4px]">{error}</div>}
          <div className="flex items-center gap-2">
            {(['email', 'otp', 'new-password', 'success'] as ForgotStep[]).map((s, i) => {
              const stepOrder = ['email', 'otp', 'new-password', 'success']
              const currentIdx = stepOrder.indexOf(step)
              return <div key={s} className="flex items-center gap-2 flex-1"><div className={`h-1.5 flex-1 rounded-full transition-colors ${i < currentIdx ? 'bg-[#0078D4]' : i === currentIdx ? 'bg-[#0078D4]/50' : 'bg-muted'}`} /></div>
            })}
          </div>
          <AnimatePresence mode="wait">
            {step === 'email' && (
              <motion.form key="email" variants={fadeUp} initial="initial" animate="animate" exit="exit" onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-1.5"><label htmlFor="reset-email" className="text-sm font-medium">Email Address</label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><input id="reset-email" name="email" type="email" required placeholder="you@company.com" className="w-full h-11 bg-secondary/50 border border-border rounded-[4px] pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/20 focus:border-[#0078D4] transition-colors placeholder:text-muted-foreground" /></div></div>
                <button type="submit" disabled={loading} className="w-full bg-[#0078D4] text-white hover:bg-[#005A9E] rounded-[4px] h-11 font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">{loading && <Loader2 className="h-4 w-4 animate-spin" />}{loading ? 'Sending code...' : 'Send Verification Code'}</button>
              </motion.form>
            )}
            {step === 'otp' && (
              <motion.form key="otp" variants={fadeUp} initial="initial" animate="animate" exit="exit" onSubmit={handleOtpSubmit} className="space-y-4">
                <div className="space-y-1.5"><label className="text-sm font-medium">Verification Code</label><div className="flex justify-center py-2"><InputOTP maxLength={6} value={otp} onChange={setOtp}><InputOTPGroup><InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} /></InputOTPGroup><InputOTPSeparator /><InputOTPGroup><InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} /></InputOTPGroup></InputOTP></div>
                  <p className="text-xs text-muted-foreground text-center pt-1">
                    Code expires in <span className="font-semibold text-foreground">{otpExpiry} minutes</span>
                  </p>
                  <p className="text-xs text-muted-foreground text-center pt-1">
                    Didn&apos;t receive the code?{' '}
                    {canResend ? (
                      <button type="button" onClick={handleResend} className="text-[#0078D4] hover:text-[#005A9E] font-medium transition-colors">Resend Code</button>
                    ) : (
                      <span className="text-muted-foreground">Resend in <span className="font-medium text-foreground tabular-nums">{countdown}s</span></span>
                    )}
                  </p>
                </div>
                <button type="submit" disabled={loading || otp.length < 6} className="w-full bg-[#0078D4] text-white hover:bg-[#005A9E] rounded-[4px] h-11 font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">{loading && <Loader2 className="h-4 w-4 animate-spin" />}{loading ? 'Verifying...' : 'Verify Code'}</button>
              </motion.form>
            )}
            {step === 'new-password' && (
              <motion.form key="new-pw" variants={fadeUp} initial="initial" animate="animate" exit="exit" onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-1.5"><label className="text-sm font-medium">New Password</label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><input type={showNewPassword ? 'text' : 'password'} required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" className="w-full h-11 bg-secondary/50 border border-border rounded-[4px] pl-10 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/20 focus:border-[#0078D4] transition-colors placeholder:text-muted-foreground" /><button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">{showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></div></div>
                <div className="space-y-1.5"><label className="text-sm font-medium">Confirm Password</label><div className="relative"><LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><input type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password" className="w-full h-11 bg-secondary/50 border border-border rounded-[4px] pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/20 focus:border-[#0078D4] transition-colors placeholder:text-muted-foreground" /></div></div>
                <button type="submit" disabled={loading} className="w-full bg-[#0078D4] text-white hover:bg-[#005A9E] rounded-[4px] h-11 font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">{loading && <Loader2 className="h-4 w-4 animate-spin" />}{loading ? 'Resetting...' : 'Reset Password'}</button>
              </motion.form>
            )}
            {step === 'success' && (
              <motion.div key="success" variants={fadeUp} initial="initial" animate="animate" exit="exit" className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center"><CheckCircle2 className="h-8 w-8 text-emerald-500" /></div>
                <p className="text-sm text-muted-foreground">You can now sign in with your new password.</p>
                <button onClick={() => setCurrentView('login')} className="w-full bg-[#0078D4] text-white hover:bg-[#005A9E] rounded-[4px] h-11 font-semibold text-sm transition-colors">Back to Sign In</button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
      {/* Right: Campaign */}
      <CampaignSide variant="login" />
    </div>
  )
}
